import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/context/AuthContext";
import {
  Search, Menu, X, Heart, LayoutDashboard,
  LogOut, ChevronDown, Sparkles,
} from "lucide-react";
import SellerAccessButton from "@/components/navbar/SellerAccessButton";
import Avatar from "@/components/Avatar";
import { TangigLockup } from "@/components/brand/TangigLogo";
import { NAV_HEIGHT } from "@/lib/navbar";
import axios from "axios";

/* ── Design tokens par thème ───────────────────────────────────
   Tant qu'on est sur le hero, la navbar n'a AUCUN fond, AUCUN flou,
   AUCUNE bordure — seule la couleur du texte change (clair/sombre).
   Une fois le hero dépassé, un fond quasi opaque + flou + une hairline
   apparaissent en fondu. Le fond était à 0.78 : le texte des pages
   traversait la barre et venait se mélanger aux onglets, et sans
   bordure la barre n'avait plus de bas. Le thème (light/dark) est
   déterminé automatiquement par la section actuellement sous la
   barre, via l'attribut HTML `data-navbar-theme="dark" | "light"`
   posé sur les sections — aucun code à ajouter dans la Navbar pour
   chaque nouvelle page. ────────────────────────────────────────── */
const THEMES = {
  light: {
    text:       "#1D1D1F",
    muted:      "#6E6E73",
    bgScrolled: "rgba(255,255,255,0.94)",
    menuBg:     "rgba(255,255,255,0.98)", // panneau menu mobile : quasi opaque pour rester lisible
    divider:    "rgba(0,0,0,0.08)",
    surface:    "#FFFFFF",
  },
  dark: {
    text:       "#F5F5F7",
    muted:      "rgba(245,245,247,0.7)",
    bgScrolled: "rgba(22,22,23,0.94)",
    menuBg:     "rgba(22,22,23,0.97)",
    divider:    "rgba(255,255,255,0.12)",
    surface:    "#1D1D1F",
  },
};

/* Menu mobile (burger) : TOUJOURS clair #F5F5F7 avec texte fonce lisible,
   quel que soit le theme de la section (sombre ou clair). N'affecte QUE le
   panneau deroulant — la barre et le desktop gardent leur comportement. */
const MENU_LIGHT = {
  menuBg:  "#FFFFFF",
  text:    "#1D1D1F",
  muted:   "#6E6E73",
  divider: "rgba(0,0,0,0.08)",
  surface: "#FFFFFF",
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();

  const [menuOpen,      setMenuOpen]      = useState(false);
  const [userMenuOpen,  setUserMenuOpen]  = useState(false);
  const [searchOpen,    setSearchOpen]    = useState(false);
  const [searchQuery,   setSearchQuery]   = useState("");
  const [favoriteCount, setFavoriteCount] = useState(0);
  const [mobileSearchFocus, setMobileSearchFocus] = useState(false);
  /* Mobile : la barre defile avec la page (absolute) au lieu de rester fixe,
     pour ne pas couvrir les textes pendant la lecture. Desktop reste fixed. */
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 1023px)").matches
  );

  /* ── Thème (light/dark) + "hero dépassé" (fond opaque) ─────────
     Dérivés automatiquement des sections marquées `data-navbar-theme`
     présentes sur la page courante, via IntersectionObserver. Pas
     d'écoute de scroll, pas de code par page au-delà de l'attribut. */
  const [theme,    setTheme]    = useState("light");
  const [pastHero, setPastHero] = useState(true);
  const searchInputRef = useRef(null);
  const mobileSearchRef = useRef(null);

  useLayoutEffect(() => {
    let heroObserver = null;
    let readTheme = null;
    let onScroll  = null;
    let rafId     = 0;
    let scheduled      = false;

    /* Detache les ecouteurs de theme : appele avant chaque reattachement et
       au demontage, sinon un changement de page en empilerait un par montage. */
    const detachTheme = () => {
      if (onScroll) {
        window.removeEventListener("scroll", onScroll);
        window.removeEventListener("resize", onScroll);
        onScroll = null;
      }
      if (rafId) { cancelAnimationFrame(rafId); rafId = 0; }
    };

    const attach = () => {
      detachTheme();
      heroObserver?.disconnect();

      /* Le footer du site est marqué `data-navbar-theme`, et il est monté sur
         TOUTES les pages. Sans ce filtre, une page qui ne marque aucune de ses
         sections (Les Systèmes) le prend pour son hero : la barre reste
         transparente en haut de page, puis redevient transparente dès que le
         footer entre dans le viewport — le contenu se retrouve à nu derrière
         les onglets. Un hero est forcément une section de la page. */
      const sections = Array.from(document.querySelectorAll("[data-navbar-theme]"))
        .filter((el) => !el.closest("footer"));

      if (sections.length === 0) {
        setTheme("light");
        setPastHero(true);
        return;
      }

      const heroEl = sections[0];

      /* Lecture synchrone immédiate — évite tout flash avant que
         l'observer ne rende son premier verdict.
         Le thème n'est PAS pose ici a partir du hero : attach() est relance
         par le MutationObserver a chaque mutation du DOM, donc en continu
         pendant les animations. Forcer le theme du hero a chaque passage
         ecrasait aussitot celui de la section reellement sous la barre.
         C'est readTheme(), appele juste apres, qui tranche. */
      const rect = heroEl.getBoundingClientRect();
      setPastHero(rect.bottom <= NAV_HEIGHT);

      /* Le thème se lit directement : quelle section marquée se trouve
         actuellement sous la barre ?
         Un IntersectionObserver le faisait avant, avec un rootMargin de
         `-NAV_HEIGHT 0 -99% 0` — soit une zone d'observation haute de
         (1 % du viewport - NAV_HEIGHT), donc NEGATIVE dès que la fenêtre
         fait moins de 4400px. Aucune section n'y entrait jamais et le thème
         restait bloqué sur celui du hero. elementFromPoint n'a pas de marge
         à calculer : il repond exactement a la question posee. */
      readTheme = () => {
        const el = document.elementFromPoint(window.innerWidth / 2, NAV_HEIGHT + 2);
        const section = el?.closest?.("[data-navbar-theme]");
        /* Aucune section marquée sous la barre : on garde le thème courant
           plutôt que de forcer le clair, sinon les pages qui ne marquent que
           leur hero clignoteraient au scroll. */
        if (section) {
          setTheme(section.dataset.navbarTheme === "dark" ? "dark" : "light");
        }
      };
      readTheme();
      onScroll = () => {
        if (rafId) return;
        rafId = requestAnimationFrame(() => { rafId = 0; readTheme(); });
      };
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll, { passive: true });

      heroObserver = new IntersectionObserver(
        ([entry]) => setPastHero(!entry.isIntersecting),
        { rootMargin: `-${NAV_HEIGHT}px 0px 0px 0px`, threshold: 0 }
      );
      heroObserver.observe(heroEl);
    };

    attach();

    /* Les pages sont montées/démontées de façon asynchrone (transitions
       Framer Motion, lazy-loading) : un MutationObserver auto-répare les
       observers dès que le DOM change, sans dépendre du timing exact
       de la navigation. */
    const scheduleAttach = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; attach(); });
    };
    const mutationObserver = new MutationObserver(scheduleAttach);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      detachTheme();
      heroObserver?.disconnect();
      mutationObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 1023px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const T = THEMES[theme];

  const isHome = location.pathname === "/";
  const accentColor = isHome ? "#3B4F7D" : "#0F766E";

  /* ── Favoris — acheteurs uniquement ── */
  useEffect(() => {
    if (!user || user.role !== "buyer") { setFavoriteCount(0); return; }
    axios
      .get(`${API}/buyer/favorites`, { withCredentials: true })
      .then((res) => {
        const count = Array.isArray(res.data)
          ? res.data.length
          : (res.data?.total ?? res.data?.assets?.length ?? 0);
        setFavoriteCount(count);
      })
      .catch((err) => console.error("Failed to fetch favorites:", err));
  }, [user]);

  /* ── Fermeture au changement de route ── */
  useEffect(() => {
    setMenuOpen(false);
    setUserMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  /* Menu mobile ouvert → scroll de la page verrouille (facon Apple / Back
     Market). Impossible de descendre tant que la croix n'a pas ferme le menu. */
  useEffect(() => {
    if (!menuOpen) return;
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, [menuOpen]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  /* La loupe mobile ouvre le menu puis focus le champ de recherche.
     On attend que le panneau soit monté (menuOpen) avant de focus. */
  useEffect(() => {
    if (menuOpen && mobileSearchFocus) {
      mobileSearchRef.current?.focus();
      setMobileSearchFocus(false);
    }
  }, [menuOpen, mobileSearchFocus]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery("");
      setSearchOpen(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getDashboardLink = () => {
    if (user?.role === "admin")  return "/admin";
    if (user?.role === "seller") return "/vendeur/dashboard";
    return "/acheteur/dashboard";
  };

  /* L'ordre suit le parcours de compréhension, pas l'importance commerciale :
     pourquoi (Vision) → quoi (Le Standard) → voir (Les Systèmes) → agir.
     « Le Standard » vient donc AVANT le catalogue : on ne peut pas juger des
     systèmes sans savoir ce que le mot recouvre. */
  const NAV_LINKS = [
    { to: "/vision",           label: "Vision"           },
    { to: "/le-standard",      label: "Le Standard"      },
    { to: "/marketplace",      label: "Les Systèmes"     },
    { to: "/devenir-createur", label: "Devenir créateur" },
  ];

  const fade = (e, dim = true) => { e.currentTarget.style.opacity = dim ? "0.6" : "1"; };

  return (
    <header
      data-testid="header"
      style={{
        position:             (isMobile && !menuOpen) ? "absolute" : "fixed",
        top:                  0,
        left:                 0,
        right:                0,
        zIndex:               9999,
        height:               `${NAV_HEIGHT}px`,
        display:              "flex",
        alignItems:           "center",
        backgroundColor:      menuOpen ? "#FFFFFF" : (pastHero ? T.bgScrolled : "transparent"),
        backdropFilter:       (!menuOpen && pastHero) ? "saturate(180%) blur(20px)" : "none",
        WebkitBackdropFilter: (!menuOpen && pastHero) ? "saturate(180%) blur(20px)" : "none",
        border:               "none",
        /* La hairline n'apparaît qu'une fois le hero dépassé : sur le hero la
           barre doit rester invisible. Elle donne un bas à la barre, sans quoi
           le contenu de la page semble commencer dans les onglets. */
        borderBottom:         (!menuOpen && pastHero) ? `1px solid ${T.divider}` : "1px solid transparent",
        boxShadow:            "none",
        transition:           "background-color 420ms ease, backdrop-filter 420ms ease, border-color 420ms ease, color 300ms ease",
      }}
    >
      <nav data-testid="navbar" style={{ width: "100%" }}>
        <div
          className="flex items-center justify-between lg:justify-center"
          style={{
            width:          "100%",
            maxWidth:       "1280px",
            margin:         "0 auto",
            padding:        "0 24px",
            gap:            "32px",
            boxSizing:      "border-box",
          }}
        >

          {/* ── Logo ────────────────────────────────────────── */}
          <Link
            to="/"
            data-testid="navbar-logo"
            aria-label="TANGIG — accueil"
            style={{
              display:        "flex",
              alignItems:     "center",
              color:          T.text,
              textDecoration: "none",
              flexShrink:     0,
              lineHeight:     1,
              opacity:        menuOpen ? 0 : 1,
              pointerEvents:  menuOpen ? "none" : "auto",
              transition:     "color 300ms ease, opacity 150ms ease",
            }}
            onMouseEnter={fade}
            onMouseLeave={(e) => fade(e, false)}
          >
            {/* Le lockup hérite de la couleur du lien, donc il bascule tout seul
                entre thème clair et sombre via T.text. 18px de capitale : en
                dessous, les trois entailles se referment optiquement. */}
            <TangigLockup size={18} aria-label={undefined} role={undefined} />
          </Link>

          {/* ── Desktop nav links ────────────────────────────── */}
          <div className="hidden lg:flex items-center" style={{ gap: "28px" }}>
            {NAV_LINKS.map((l) => {
              const isActive = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className="flex items-center gap-1.5"
                  style={{
                    fontFamily:     "Inter, sans-serif",
                    fontSize:       "12.5px",
                    fontWeight:     isActive ? 600 : 400,
                    color:          isActive ? accentColor : T.text,
                    textDecoration: "none",
                    transition:     "color 300ms ease, opacity 150ms ease",
                  }}
                  onMouseEnter={fade}
                  onMouseLeave={(e) => fade(e, false)}
                >
                  {l.badge && (
                    <Sparkles className="w-[11px] h-[11px]" style={{ color: accentColor }} />
                  )}
                  {l.label}
                </Link>
              );
            })}
          </div>

          {/* ── Search + Auth ────────────────────────────────── */}
          <div className="hidden lg:flex items-center" style={{ gap: "22px", flexShrink: 0 }}>

            {/* Search — icône seule, se déplie au clic (comme apple.com).
                Fond opaque propre dès l'ouverture : la navbar elle-même
                reste transparente au-dessus du hero, donc le champ a besoin
                de sa propre surface pour que le texte tapé reste lisible
                quoi qu'il y ait derrière (image, cheveux, etc.). */}
            <form
              onSubmit={handleSearch}
              className="flex items-center"
              style={{
                backgroundColor: searchOpen ? T.bgScrolled : "transparent",
                backdropFilter:  searchOpen ? "blur(20px) saturate(180%)" : "none",
                WebkitBackdropFilter: searchOpen ? "blur(20px) saturate(180%)" : "none",
                borderRadius: "99px",
                padding: searchOpen ? "5px 6px 5px 12px" : "0",
                transition: "background-color 250ms ease, padding 250ms ease",
              }}
            >
              <input
                ref={searchInputRef}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                placeholder="Rechercher…"
                aria-hidden={!searchOpen}
                tabIndex={searchOpen ? 0 : -1}
                style={{
                  width:       searchOpen ? "150px" : "0px",
                  opacity:     searchOpen ? 1 : 0,
                  border:      "none",
                  outline:     "none",
                  background:  "transparent",
                  color:       T.text,
                  fontFamily:  "Inter, sans-serif",
                  fontSize:    "13px",
                  transition:  "width 250ms ease, opacity 150ms ease",
                }}
                data-testid="navbar-search"
              />
              <button
                type="button"
                onClick={() => setSearchOpen((v) => !v)}
                aria-label="Rechercher"
                style={{
                  display:    "flex",
                  alignItems: "center",
                  background: "none",
                  border:     "none",
                  padding:    "6px",
                  cursor:     "pointer",
                  color:      T.text,
                  flexShrink: 0,
                  transition: "color 300ms ease, opacity 150ms ease",
                }}
                onMouseEnter={fade}
                onMouseLeave={(e) => fade(e, false)}
              >
                <Search className="w-4 h-4" />
              </button>
            </form>

            {/* Espace vendeur / Devenir vendeur */}
            <SellerAccessButton user={user} theme={theme} />

            {/* Accès direct au tableau de bord — masqué pour le vendeur
                car « Espace vendeur » y mène déjà (pas de doublon). */}
            {user && user.role !== "seller" && (
              <Link
                to={getDashboardLink()}
                className="flex items-center"
                style={{
                  fontFamily:     "Inter, sans-serif",
                  fontSize:       "12.5px",
                  fontWeight:     location.pathname.startsWith(getDashboardLink()) ? 700 : 400,
                  color:          location.pathname.startsWith(getDashboardLink()) ? accentColor : T.text,
                  textDecoration: "none",
                  transition:     "color 300ms ease, opacity 150ms ease",
                }}
                onMouseEnter={fade}
                onMouseLeave={(e) => fade(e, false)}
                data-testid="nav-dashboard-direct"
              >
                Tableau de bord
              </Link>
            )}

            {user ? (
              <>
                {/* Favoris — acheteurs uniquement */}
                {user.role === "buyer" && (
                  <Link
                    to="/acheteur/dashboard"
                    className="relative flex items-center"
                    style={{ color: T.text, transition: "color 300ms ease, opacity 150ms ease" }}
                    title="Mes favoris"
                    onMouseEnter={fade}
                    onMouseLeave={(e) => fade(e, false)}
                  >
                    <Heart style={{ width: "16px", height: "16px" }} />
                    {favoriteCount > 0 && (
                      <span
                        className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-white text-[9px] flex items-center justify-center"
                        style={{ backgroundColor: accentColor, fontFamily: "Inter, sans-serif" }}
                      >
                        {favoriteCount > 9 ? "9+" : favoriteCount}
                      </span>
                    )}
                  </Link>
                )}

                {/* Menu utilisateur */}
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-1.5 focus:outline-none"
                    style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                    data-testid="user-menu-btn"
                  >
                    <Avatar name={user.name} picture={user.picture} size={20} />
                    <span style={{ fontFamily: "Inter, sans-serif", fontSize: "12.5px", fontWeight: 500, color: T.text, transition: "color 300ms ease" }}>
                      {user.name?.split(" ")[0]}
                    </span>
                    <ChevronDown
                      className={`w-3 h-3 transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`}
                      style={{ color: T.muted }}
                    />
                  </button>

                  {userMenuOpen && (
                    <div
                      className="absolute right-0 mt-3 w-52 overflow-hidden z-50 animate-fade-in-down"
                      style={{
                        backgroundColor: T.surface,
                        borderRadius:    "16px",
                        boxShadow:       "0 12px 40px rgba(0,0,0,0.18)",
                      }}
                    >
                      <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.divider}` }}>
                        <p style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", fontWeight: 600, color: T.text }} className="truncate">
                          {user.name}
                        </p>
                        <p style={{ fontFamily: "Inter, sans-serif", fontSize: "11px", color: T.muted }} className="truncate mt-0.5">
                          {user.email}
                        </p>
                      </div>
                      <Link
                        to={getDashboardLink()}
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 transition-colors"
                        style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", color: T.text, textDecoration: "none" }}
                        data-testid="nav-dashboard-link"
                      >
                        <LayoutDashboard className="w-3.5 h-3.5" style={{ color: T.muted }} />
                        Mon tableau de bord
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-red-500 transition-colors"
                        style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", borderTop: `1px solid ${T.divider}` }}
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-3.5 h-3.5" />
                        Se déconnecter
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              /* Login button — texte seul, pas de contour */
              <Link
                to="/connexion"
                style={{
                  fontFamily:     "Inter, sans-serif",
                  color:          T.text,
                  fontSize:       "12.5px",
                  fontWeight:     500,
                  textDecoration: "none",
                  transition:     "color 300ms ease, opacity 150ms ease",
                }}
                onMouseEnter={fade}
                onMouseLeave={(e) => fade(e, false)}
                data-testid="nav-login-btn"
              >
                Connexion
              </Link>
            )}
          </div>

          {/* ── Actions mobile : loupe + hamburger (pattern Apple) ─── */}
          <div className="lg:hidden flex items-center" style={{ gap: "20px" }}>
            {/* Loupe — ouvre le menu mobile avec le champ recherche focus.
                Masquée quand le menu est ouvert (le champ y est déjà visible). */}
            {!menuOpen && (
              <button
                type="button"
                aria-label="Rechercher"
                className="flex items-center focus:outline-none"
                style={{ color: T.text, background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
                onClick={() => { setMenuOpen(true); setMobileSearchFocus(true); }}
              >
                <Search className="w-4 h-4" />
              </button>
            )}

            {/* Favoris ❤️ — acheteurs connectés uniquement */}
            {user?.role === "buyer" && !menuOpen && (
              <Link
                to="/acheteur/dashboard"
                aria-label="Mes favoris"
                title="Mes favoris"
                className="relative flex items-center focus:outline-none"
                style={{ color: T.text }}
              >
                <Heart className="w-4 h-4" />
                {favoriteCount > 0 && (
                  <span
                    className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full text-white text-[9px] flex items-center justify-center"
                    style={{ backgroundColor: accentColor, fontFamily: "Inter, sans-serif" }}
                  >
                    {favoriteCount > 9 ? "9+" : favoriteCount}
                  </span>
                )}
              </Link>
            )}

            {/* Hamburger */}
            <button
              className="flex items-center focus:outline-none"
              style={{ color: menuOpen ? "#1D1D1F" : T.text, background: "transparent", border: "none", padding: 0 }}
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* ── Menu mobile ──────────────────────────────────── */}
        {menuOpen && (() => {
          /* T local = theme clair force pour tout le panneau (voir MENU_LIGHT). */
          const T = MENU_LIGHT;
          return (
          <div
            className="lg:hidden animate-fade-in-down"
            style={{
              backgroundColor:      T.menuBg,
              backdropFilter:       "saturate(180%) blur(20px)",
              WebkitBackdropFilter: "saturate(180%) blur(20px)",
              padding: "16px 20px 24px",
              position: "fixed",
              top: `${NAV_HEIGHT}px`,
              left: 0,
              right: 0,
              zIndex: 9998,
              boxShadow: "none",
              minHeight: `calc(100vh - ${NAV_HEIGHT}px)`,
              maxHeight: `calc(100vh - ${NAV_HEIGHT}px)`,
              overflowY: "auto"
            }}
          >
            {/* Mobile search — pill compacte facon Back Market (gris clair sur blanc) */}
            <form onSubmit={handleSearch} className="mb-4">
              <div className="relative" style={{ maxWidth: 260 }}>
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: T.muted }} />
                <input
                  ref={mobileSearchRef}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Rechercher un système…"
                  className="w-full rounded-full text-[14px] focus:outline-none"
                  style={{
                    fontFamily:      "Inter, sans-serif",
                    backgroundColor: "#F5F5F7",
                    border:          "none",
                    padding:         "11px 16px 11px 42px",
                    color:           T.text,
                  }}
                />
              </div>
            </form>

            {/* Mobile nav links */}
            {NAV_LINKS.map((l) => {
              const isActive = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl transition-colors"
                  style={{
                    fontFamily:      "Inter, sans-serif",
                    fontSize:        "19px",
                    fontWeight:      isActive ? 600 : 500,
                    color:           isActive ? accentColor : T.text,
                    textDecoration:  "none",
                  }}
                  onClick={() => setMenuOpen(false)}
                >
                  {l.badge && <Sparkles className="w-3 h-3" style={{ color: accentColor }} />}
                  {l.label}
                </Link>
              );
            })}

            {/* Mobile auth section */}
            <div style={{ paddingTop: "12px", marginTop: "8px" }}>
              {user && (
                <div className="mb-2">
                  <SellerAccessButton user={user} theme="light" />
                </div>
              )}
              {user ? (
                <>
                  <div className="flex items-center gap-3 px-1 py-2 mb-1">
                    <Avatar name={user.name} picture={user.picture} size={28} />
                    <div>
                      <p style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", fontWeight: 600, color: T.text }}>
                        {user.name}
                      </p>
                      <p style={{ fontFamily: "Inter, sans-serif", fontSize: "11px", color: T.muted }}>
                        {user.email}
                      </p>
                    </div>
                  </div>
                  <Link
                    to={getDashboardLink()}
                    className="flex items-center gap-2.5 px-3 py-2 rounded-xl transition-colors"
                    style={{ fontFamily: "Inter, sans-serif", fontSize: "13px", color: T.text, textDecoration: "none" }}
                    onClick={() => setMenuOpen(false)}
                  >
                    <LayoutDashboard className="w-3.5 h-3.5" style={{ color: T.muted }} />
                    Mon tableau de bord
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-red-500 rounded-xl transition-colors"
                    style={{ fontFamily: "Inter, sans-serif", fontSize: "13px" }}
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Se déconnecter
                  </button>
                </>
              ) : (
                <Link
                  to="/connexion"
                  className="flex items-center px-3 py-2.5 focus:outline-none"
                  style={{
                    fontFamily:     "Inter, sans-serif",
                    fontSize:       "19px",
                    fontWeight:     500,
                    color:          T.text,
                    textDecoration: "none",
                  }}
                  onClick={() => setMenuOpen(false)}
                >
                  Connexion
                </Link>
              )}
            </div>
          </div>
          );
        })()}
      </nav>
    </header>
  );
}
